import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { authAPI } from '../../services/api';

const PROVINCES: { value: string; label: string }[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

// Employee self-service profile. Province and weekly hours are editable; salary
// is read-only here (only admins/management can change it via user management).
const ProfileDetails: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { showToast } = useToast();

  const [province, setProvince] = useState<string>(user?.province_code || '');
  const [hoursPerWeek, setHoursPerWeek] = useState<string>(
    user?.hours_per_week != null ? String(user.hours_per_week) : '',
  );
  const [saving, setSaving] = useState(false);

  const label = isDark ? 'text-slate-300' : 'text-gray-700';
  const input = isDark
    ? 'bg-slate-900 border-slate-700 text-white'
    : 'bg-white border-gray-300 text-gray-900';
  const readonly = isDark
    ? 'bg-slate-800 border-slate-700 text-slate-400'
    : 'bg-gray-100 border-gray-200 text-gray-500';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAPI.updateProfile({
        province_code: province || null,
        hours_per_week: hoursPerWeek === '' ? null : Number(hoursPerWeek),
      });
      showToast(
        t('profile.saved', { defaultValue: 'Profile updated.' }),
        'success',
      );
    } catch (err: any) {
      showToast(
        err.response?.data?.error ||
          t('profile.saveFailed', { defaultValue: 'Could not update profile.' }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-5 max-w-lg">
      <div>
        <label className={`block text-sm font-medium ${label} mb-1`}>
          {t('users.province', { defaultValue: 'Province' })}
        </label>
        <select
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${input}`}
        >
          <option value="">
            {t('users.selectProvince', { defaultValue: 'Select a province…' })}
          </option>
          {PROVINCES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={`block text-sm font-medium ${label} mb-1`}>
          {t('users.hoursPerWeek', { defaultValue: 'Hours per week' })}
        </label>
        <input
          type="number"
          min="0"
          step="0.5"
          value={hoursPerWeek}
          onChange={(e) => setHoursPerWeek(e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${input}`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${label} mb-1`}>
          {t('users.salary', { defaultValue: 'Annual salary' })}
        </label>
        <input
          type="text"
          readOnly
          disabled
          value={user?.salary != null ? String(user.salary) : '—'}
          className={`w-full px-3 py-2 border rounded-lg cursor-not-allowed ${readonly}`}
        />
        <p className={`mt-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
          {t('profile.salaryReadonly', {
            defaultValue: 'Managed by your administrator.',
          })}
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-xl disabled:opacity-50"
        >
          {saving ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </form>
  );
};

export default ProfileDetails;
